import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Typography,
  Button,
  Box,
  FormControl,
  Select,
  FormHelperText,
  useTheme,
  useMediaQuery,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // ← Added
import { useSnackbar } from "../../App";
import { getAllBranchLists } from "../../service/branchService";
import { createSalesMan, updateSalesMan } from "../../service/salesmanService";
import Color from "../../components/Color";

const DEFAULT = {
  name: "",
  branch_id: "",
  mobile: "",
  id: undefined,
};

const SalesMan = ({
  open,
  onClose,
  formData: propFormData,
  setFormData: setPropFormData,
  onSubmit,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [local, setLocal] = useState(DEFAULT);
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  /* ────────────────────── Sync prop data ────────────────────── */
  useEffect(() => {
    if (open) {
      setLocal({ ...DEFAULT, ...propFormData });
      setErrors({});
    }
  }, [open, propFormData]);

  /* ────────────────────── Load branches ────────────────────── */
  useEffect(() => {
    if (!open) return;

    const fetchBranches = async () => {
      try {
        const res = await getAllBranchLists();
        setBranches(Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch {
        showSnackbar("Failed to fetch branches!", "error");
      }
    };

    fetchBranches();
  }, [open, showSnackbar]);

  /* ────────────────────── Handle Input Change ────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const filtered = value.replace(/[^a-zA-Z ]/g, "");
      const limitedValue = filtered.slice(0, 50);

      setLocal((prev) => ({ ...prev, name: limitedValue }));

      if (!/^[a-zA-Z ]*$/.test(value)) {
        setErrors((prev) => ({ ...prev, name: ["Only letters are allowed"] }));
      } else if (!limitedValue.trim()) {
        setErrors((prev) => ({ ...prev, name: ["Name is required"] }));
      } else if (limitedValue.length > 50) {
        setErrors((prev) => ({ ...prev, name: ["Name must not exceed 50 characters"] }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (name === "mobile") {
      const numericValue = value.replace(/[^0-9]/g, "").slice(0, 10);
      setLocal((prev) => ({ ...prev, mobile: numericValue }));

      if (numericValue.length === 10) {
        if (/^(\d)\1{9}$/.test(numericValue)) {
          setErrors((prev) => ({ ...prev, mobile: ["Enter a valid mobile number"] }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.mobile;
            return newErrors;
          });
        }
      } else if (numericValue.length > 0) {
        setErrors((prev) => ({ ...prev, mobile: ["Phone must be exactly 10 digits"] }));
      } else {
        setErrors((prev) => ({ ...prev, mobile: ["Phone number is required"] }));
      }
    } else if (name === "branch_id") {
      setLocal((prev) => ({ ...prev, branch_id: value }));
      if (!value) {
        setErrors((prev) => ({ ...prev, branch_id: ["Select a branch"] }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.branch_id;
          return newErrors;
        });
      }
    }
  };

  /* ────────────────────── Full Validation ────────────────────── */
  const validateAll = () => {
    const newErrors = {};

    if (!local.name?.trim()) {
      newErrors.name = ["Name is required"];
    } else if (!/^[a-zA-Z ]+$/.test(local.name)) {
      newErrors.name = ["Only letters are allowed"];
    } else if (local.name.length > 50) {
      newErrors.name = ["Name must not exceed 50 characters"];
    }

    if (!local.branch_id) {
      newErrors.branch_id = ["Select a branch"];
    }

    const phone = local.mobile?.trim();
    if (!phone) {
      newErrors.mobile = ["Phone number is required"];
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.mobile = ["Phone must be exactly 10 digits"];
    } else if (/^(\d)\1{9}$/.test(phone)) {
      newErrors.mobile = ["Enter a valid mobile number"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ────────────────────── Submit ────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: local.name.trim(),
        branch_id: local.branch_id,
        mobile: local.mobile.trim(),
      };

      if (local.id) {
        await updateSalesMan(local.id, payload);
        showSnackbar("Salesperson updated successfully!", "success");
      } else {
        await createSalesMan(payload);
        showSnackbar("Salesperson created successfully!", "success");
      }

      onSubmit?.(); // Refresh parent list
      handleClose();
    } catch (err) {
      const serverErrors = err?.response?.data?.errors || {};
      const formatted = Object.fromEntries(
        Object.entries(serverErrors).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])
      );
      setErrors((prev) => ({ ...prev, ...formatted }));
      showSnackbar(err?.response?.data?.message || "Save failed", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ────────────────────── Close & Reset ────────────────────── */
  const handleClose = () => {
    setLocal(DEFAULT);
    setErrors({});
    onClose();
  };

  const isEdit = !!local.id;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (loading) return;
        handleClose();
      }}
      disableEscapeKeyDown={loading}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          width: isMobile ? "90vw" : "380px",
          maxWidth: "95vw",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: isMobile ? "1rem" : "1.25rem",
          position: "relative",
          pr: 6,
        }}
      >
        {isEdit ? "Edit Salesperson" : "Add Salesperson"}

        {/* Close Icon Button */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <form onSubmit={handleSubmit} noValidate>
          <Box sx={{ display: "grid", gap: 2 }}>
            {/* Name */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Salesperson Name*
              </Typography>
              <TextField
                name="name"
                fullWidth
                value={local.name}
                onChange={handleChange}
                placeholder="Enter name"
                error={!!errors.name}
                helperText={errors.name?.[0] || `${local.name.length}/50`}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 50 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Branch */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Branch*
              </Typography>
              <FormControl fullWidth error={!!errors.branch_id}>
                <Select
                  name="branch_id"
                  value={local.branch_id ?? ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return <span style={{ color: "#9e9e9e" }}>Select branch</span>;
                    return branches.find((b) => b.id === selected)?.name || "";
                  }}
                  sx={{
                    "& .MuiSelect-select": { fontSize: isMobile ? "0.85rem" : "1rem" },
                    height: 40,
                  }}
                >
                  <MenuItem value="" disabled>
                    Select branch
                  </MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.branch_id && (
                  <FormHelperText>{errors.branch_id[0]}</FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Mobile */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Phone Number*
              </Typography>
              <TextField
                name="mobile"
                fullWidth
                value={local.mobile}
                onChange={handleChange}
                placeholder="Enter 10-digit mobile number"
                error={!!errors.mobile}
                helperText={errors.mobile?.[0]}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                type="tel"
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>
          </Box>

          {/* Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button
              onClick={handleClose}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              sx={{
                background: Color.primary.main,
                color: "white",
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
                "&:hover": { background: Color.primary.dark },
                "&.Mui-disabled": {
                  background: Color.primary.main,
                  opacity: 0.7,
                  color: "white",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : isEdit ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SalesMan;